import { consoleViewGroupOf, type ConsoleGroup, type ConsoleViewGroup } from './consoleGroup';
import type { HuddleStatus } from './huddleApi';

/**
 * What the TAP Console tabs, status dropdown and Done fold show (Kyu, 30 Sept,
 * decision B). Pure, so the rules are tested without a browser.
 *
 * Alex's page: the list is the OPEN tasks of the active tab whose status is
 * the dropdown's; Done lives only in a fold of the active tab's done tasks.
 * Kyu's My Day section: a header tab (All or a group) narrows both its open
 * rows and its own Completed fold. Filtering never sorts: payload and list
 * order are kept. Untagged reads as Others through consoleGroupOf.
 */

export const CONSOLE_STATUS_LABELS: Record<HuddleStatus, string> = {
  queue: 'Needs you',
  in_progress: 'In Progress',
  done: 'Done',
};

/** The dropdown. No Done choice: done is an archive, not a working view. */
export type ConsoleStatusChoice = Exclude<HuddleStatus, 'done'>;

export const CONSOLE_STATUS_CHOICES: readonly { key: ConsoleStatusChoice; label: string }[] = (
  ['queue', 'in_progress'] as const
).map((key) => ({ key, label: CONSOLE_STATUS_LABELS[key] }));

export const DEFAULT_CONSOLE_STATUS: ConsoleStatusChoice = 'queue';
export const DEFAULT_CONSOLE_GROUP: ConsoleGroup = 'sales_cg';

export function consoleView<T extends { status: HuddleStatus; title: string; group?: unknown }>(
  tasks: readonly T[],
  group: ConsoleViewGroup,
  status: ConsoleStatusChoice,
): { open: T[]; done: T[] } {
  const inTab = tasks.filter((task) => consoleViewGroupOf(task) === group);
  return {
    // The LasVegas tab ignores the dropdown, so nothing in it can hide.
    open: inTab.filter((task) =>
      group === 'trip' ? task.status !== 'done' : task.status === status,
    ),
    done: inTab.filter((task) => task.status === 'done'),
  };
}

/** The number on each tab: the length of the list that tab shows. */
export function consoleTabCounts<
  T extends { status: HuddleStatus; title: string; group?: unknown },
>(tasks: readonly T[], status: ConsoleStatusChoice): Record<ConsoleViewGroup, number> {
  const counts: Record<ConsoleViewGroup, number> = {
    sales_cg: 0,
    admin: 0,
    others: 0,
    trip: 0,
    parked: 0,
  };
  for (const task of tasks) {
    const key = consoleViewGroupOf(task);
    if (key === 'trip' ? task.status !== 'done' : task.status === status) counts[key] += 1;
  }
  return counts;
}

/** My Day's header tab: every group, the LasVegas, or one group. */
export type ConsoleTab = ConsoleViewGroup | 'all';

/** My Day shows the LasVegas tab only while a trip task is still open. */
export function consoleHasOpenTrip(
  list: readonly { is_completed: boolean; title: string }[],
): boolean {
  return list.some((task) => !task.is_completed && consoleViewGroupOf(task) === 'trip');
}

export function consoleSection<
  T extends { is_completed: boolean; title: string; console_group: unknown },
>(list: readonly T[], doneEarlier: readonly T[], tab: ConsoleTab): { open: T[]; done: T[] } {
  // All is every tab except Parked (Kyu, 2 Nov): a parked task waits in its own tab.
  const shown = (task: T) => {
    const group = consoleViewGroupOf({ title: task.title, group: task.console_group });
    return tab === 'all' ? group !== 'parked' : group === tab;
  };
  return {
    open: list.filter((task) => !task.is_completed && shown(task)),
    done: [...list.filter((task) => task.is_completed), ...doneEarlier].filter(shown),
  };
}
